import axios from "axios";
import axiosRetry from "axios-retry";
import endpoints from "./endpoints";
import {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearAuthCookies,
} from "@utils/cookies";

// Get the base URL based on environment
const getBaseURL = () => {
  const baseURL = process.env.REACT_APP_API_BASE_URL || "";
  return `${baseURL}/api`;
};

const api = axios.create({
  baseURL: getBaseURL(),
  // Without a timeout axios waits forever, so a stalled backend leaves the UI
  // spinning with no error and axios-retry never fires. 60s is above a normal
  // slow NetSuite-backed page but well under gunicorn's worker timeout.
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Configure global retry logic for 429 errors
axiosRetry(api, {
  // 5 retries turned one stalled request into six, multiplying load on the
  // very backend that was already saturated. 2 still covers transient 429s.
  retries: process.env.NODE_ENV === "production" ? 2 : 3,
  retryDelay: (retryCount, error) => {
    // Use retry-after header if provided by server
    const retryAfter = error.response?.headers["retry-after"];
    if (retryAfter && error.response?.status === 429) {
      return parseInt(retryAfter) * 1000;
    }
    // More conservative backoff on production
    const baseDelay = process.env.NODE_ENV === "production" ? 2000 : 1000;
    return Math.min(baseDelay * Math.pow(2, retryCount), 30000); // Cap at 30s
  },
  retryCondition: (error) => {
    // Retry on 429 (rate limit) or network/idempotent errors
    return error.response?.status === 429 || axiosRetry.isNetworkOrIdempotentRequestError(error);
  },
});

// Add request interceptor to attach access token
api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Add response interceptor to handle token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh logic for auth endpoints (let the error propagate)
    const isAuthEndpoint =
      originalRequest.url?.includes("auth/login") ||
      originalRequest.url?.includes("auth/register") ||
      originalRequest.url?.includes("auth/token/refresh");

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        // No refresh token - reset state and reject
        isRefreshing = false;
        processQueue(error, null);
        return Promise.reject(error);
      }

      try {
        // Use a plain axios instance to avoid sending the expired access token
        // (the request interceptor would attach the expired Bearer token, causing a 401)
        const response = await axios.post(`${getBaseURL()}/${endpoints.AUTH_REFRESH}`, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;
        setAccessToken(access);

        // Update refresh token if backend rotates it (ROTATE_REFRESH_TOKENS: True)
        if (refresh) {
          setRefreshToken(refresh);
        }

        // Update authorization header
        api.defaults.headers.common.Authorization = `Bearer ${access}`;
        originalRequest.headers.Authorization = `Bearer ${access}`;

        processQueue(null, access);
        isRefreshing = false;

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh failed, clear all auth data and redirect to login
        clearAuthCookies();

        // Reject first so React Query / loading states settle immediately
        const authError = new Error("Session expired. Please log in again.");
        authError.isAuthError = true;
        authError.response = refreshError.response;

        // Redirect after a tick so the rejection propagates to all callers first
        setTimeout(() => {
          window.location.href = "/login";
        }, 0);

        return Promise.reject(authError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
